import { Building2, Target, Shield, CheckCircle2 } from "lucide-react";
import DarkPageHero from "../components/DarkPageHero";

const FINANCIFY_LOGO = "https://customer-assets.emergentagent.com/job_cf39cd38-3e4c-4cdf-8fd4-859e0591d26f/artifacts/0ftkiwrk_IMG_2860.png";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <DarkPageHero
        overline="ABOUT FINANCIFY"
        title={<>Simplifying loan pre-qualification <span className="text-[#52BFB8]">for the Caribbean</span>.</>}
        subtitle="A Bahamian-built fintech designed to help you understand your options without leaving home."
      />

      {/* Mission */}
      <section className="py-16">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-10 mb-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(82, 191, 184, 0.15)' }}>
                  <Target className="w-6 h-6" style={{ color: '#52BFB8' }} />
                </div>
                <h2 className="text-2xl font-bold" style={{ color: '#2D3748' }}>Our Mission</h2>
              </div>
              <p className="text-gray-600 text-lg leading-relaxed">
                Financify was created to simplify the loan pre-qualification process for Bahamian residents. 
                We believe everyone deserves transparent access to financial information without the hassle 
                of visiting multiple banks or worrying about credit inquiries.
              </p>
            </div>

            {/* What We Do */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-10 mb-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(82, 191, 184, 0.15)' }}>
                  <Building2 className="w-6 h-6" style={{ color: '#52BFB8' }} />
                </div>
                <h2 className="text-2xl font-bold" style={{ color: '#2D3748' }}>What We Do</h2>
              </div>
              <div className="space-y-4 text-gray-600">
                <p>
                  Financify is a digital pre-qualification platform that helps you understand your 
                  potential loan eligibility before formally applying to banks.
                </p>
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                  <p className="text-amber-800 font-medium">
                    Important: We are not a lender and do not make loan decisions.
                  </p>
                </div>
                <ul className="space-y-3 mt-6">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: '#52BFB8' }}></span>
                    <span>We evaluate your information against general bank lending criteria</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: '#52BFB8' }}></span>
                    <span>We provide estimated loan amounts, payments, and interest rates</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: '#52BFB8' }}></span>
                    <span>We connect you with appropriate lending institutions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: '#52BFB8' }}></span>
                    <span>We never share your data without your explicit consent</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Our Promise Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-10 mb-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(82, 191, 184, 0.15)' }}>
                  <CheckCircle2 className="w-6 h-6" style={{ color: '#52BFB8' }} />
                </div>
                <h2 className="text-2xl font-bold" style={{ color: '#2D3748' }}>Our Promise</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#52BFB8]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#52BFB8] font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#2D3748' }}>Transparency First</h3>
                    <p className="text-gray-600 text-sm">
                      We show you exactly why you qualify or don't - no hidden criteria or surprises.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#52BFB8]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#52BFB8] font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#2D3748' }}>Save Your Time</h3>
                    <p className="text-gray-600 text-sm">
                      Compare multiple banks in minutes instead of spending days visiting branches.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#52BFB8]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#52BFB8] font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#2D3748' }}>Local Knowledge</h3>
                    <p className="text-gray-600 text-sm">
                      Our engine is built specifically for Bahamian banks and their unique lending criteria.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#52BFB8]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#52BFB8] font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#2D3748' }}>Your Data, Protected</h3>
                    <p className="text-gray-600 text-sm">
                      We never share your information without consent - privacy is our priority.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust & Security */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(82, 191, 184, 0.15)' }}>
                  <Shield className="w-6 h-6" style={{ color: '#52BFB8' }} />
                </div>
                <h2 className="text-2xl font-bold" style={{ color: '#2D3748' }}>Trust & Security</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold mb-2" style={{ color: '#2D3748' }}>Registered Business</h3>
                  <p className="text-sm text-gray-600">
                    Financify is a registered business in The Commonwealth of The Bahamas.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold mb-2" style={{ color: '#2D3748' }}>Data Protection</h3>
                  <p className="text-sm text-gray-600">
                    Your data is encrypted with 256-bit SSL and never shared without consent.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold mb-2" style={{ color: '#2D3748' }}>No Credit Impact</h3>
                  <p className="text-sm text-gray-600">
                    Our pre-qualification checks do not affect your CRIF credit report.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold mb-2" style={{ color: '#2D3748' }}>Transparent Process</h3>
                  <p className="text-sm text-gray-600">
                    We clearly explain what we do and don't do. No hidden fees or surprises.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
